/**
 * AI Hallucinations Check Module
 * Detects code that may have been generated incorrectly by AI
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Common AI hallucination patterns

// Non-existent .NET namespaces/types
const FAKE_DOTNET_NAMESPACES = [
  'System.AI',
  'System.Machine',
  'Microsoft.AI.OpenAI',
  'Microsoft.Extensions.AI',
  'Azure.AI.TextAnalytics', // Often confused
  'System.Data.Linq', // Obsolete
];

// Non-existent npm packages (commonly hallucinated)
const FAKE_NPM_PACKAGES = [
  '@react/router', // Should be react-router-dom
  '@microsoft/auth', // Various incorrect forms
  'react-query/v4', // Incorrect path
  '@tanstack/query', // Should be @tanstack/react-query
];

// Patterns that suggest AI-generated incorrect code
const AI_CODE_PATTERNS = [
  {
    pattern: /\/\/\s*TODO:\s*implement/gi,
    name: 'Unimplemented TODO',
    description: 'AI-generated placeholder that was not completed',
  },
  {
    pattern: /throw\s+new\s+NotImplementedException\s*\(\s*\)/gi,
    name: 'NotImplementedException',
    description: 'Method stub that was not implemented',
  },
  {
    pattern: /\/\/\s*\.\.\./gi,
    name: 'Ellipsis comment',
    description: 'AI placeholder comment indicating incomplete code',
  },
  {
    pattern: /\/\*\s*\.\.\.\s*\*\//gi,
    name: 'Ellipsis block comment',
    description: 'AI placeholder indicating incomplete code',
  },
];

// Methods that don't exist on common types
const FAKE_METHODS: Record<string, string[]> = {
  string: ['IsEmpty', 'IsNotEmpty', 'ToInt', 'ToDouble', 'SafeTrim'],
  List: ['AddIfNotExists', 'SafeAdd', 'GetOrDefault', 'FindOrCreate'],
  Dictionary: ['GetOrAdd', 'TryAdd', 'SafeGet'], // Some exist in ConcurrentDictionary
  Task: ['WaitAll', 'WhenAny'], // Should be Task.WaitAll, Task.WhenAny (static)
  DateTime: ['ToISOString', 'FromISOString'], // JavaScript confusion
};

// C# using statements that might be hallucinated
const CSHARP_USING_PATTERN = /using\s+([\w.]+);/gi;

// TypeScript/JavaScript import patterns
const TS_IMPORT_PATTERN = /import\s+.*\s+from\s+['"]([^'"]+)['"]/gi;
// TS_REQUIRE_PATTERN reserved for future use: /require\s*\(\s*['"]([^'"]+)['"]\s*\)/gi

export async function checkAiHallucinations(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  // Build index of existing files for import validation
  const existingFiles = new Set(context.files.map((f) => f.relativePath.toLowerCase()));
  const existingModules = new Set<string>();

  for (const file of context.files) {
    // Extract module names for later validation
    if (file.language === 'csharp') {
      const namespaceMatch = /namespace\s+([\w.]+)/gi.exec(file.content);
      if (namespaceMatch) {
        existingModules.add(namespaceMatch[1].toLowerCase());
      }
    }
  }

  for (const file of context.files) {
    const lines = file.content.split('\n');

    // Check for AI code patterns
    for (const { pattern, name, description } of AI_CODE_PATTERNS) {
      let match;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);

        findings.push({
          id: generateFindingId('ai-hallucinations'),
          category: 'ai-hallucinations',
          severity: 'critical',
          title: `Incomplete Code: ${name}`,
          description: description,
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
          suggestion: 'Complete the implementation or remove the placeholder.',
          autoFixable: false,
        });
      }
    }

    // C# specific checks
    if (file.language === 'csharp') {
      // Check for fake namespaces
      let match;
      CSHARP_USING_PATTERN.lastIndex = 0;
      while ((match = CSHARP_USING_PATTERN.exec(file.content)) !== null) {
        const namespace = match[1];

        for (const fakeNs of FAKE_DOTNET_NAMESPACES) {
          if (namespace.startsWith(fakeNs)) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('ai-hallucinations'),
              category: 'ai-hallucinations',
              severity: 'blocking',
              title: 'Non-existent Namespace',
              description: `The namespace '${namespace}' does not exist in .NET. This may be AI-hallucinated code.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(match[0]),
              suggestion: 'Verify this namespace exists. Check NuGet packages or use the correct namespace.',
              autoFixable: false,
            });
          }
        }
      }

      // Check for fake methods on common types
      for (const [typeName, fakeMethods] of Object.entries(FAKE_METHODS)) {
        for (const fakeMethod of fakeMethods) {
          const methodPattern = new RegExp(`\\.${fakeMethod}\\s*\\(`, 'gi');
          let methodMatch;
          while ((methodMatch = methodPattern.exec(file.content)) !== null) {
            const lineNumber = getLineNumber(file.content, methodMatch.index);
            const lineContent = lines[lineNumber - 1]?.trim() || '';

            // Check if it's likely on the type we're looking for
            const contextStart = Math.max(0, methodMatch.index - 50);
            const context = file.content.slice(contextStart, methodMatch.index).toLowerCase();

            if (context.includes(typeName.toLowerCase()) || typeName === 'string') {
              findings.push({
                id: generateFindingId('ai-hallucinations'),
                category: 'ai-hallucinations',
                severity: 'blocking',
                title: `Non-existent Method: ${fakeMethod}`,
                description: `The method '${fakeMethod}' does not exist on ${typeName} in .NET. This is likely AI-hallucinated code.`,
                file: file.relativePath,
                line: lineNumber,
                code: truncateCode(lineContent),
                suggestion: `Use the correct .NET method. For example: string.IsNullOrEmpty() instead of IsEmpty().`,
                autoFixable: false,
              });
            }
          }
        }
      }
    }

    // TypeScript/JavaScript checks
    if (['typescript', 'javascript', 'tsx', 'jsx'].includes(file.language)) {
      // Check for fake npm packages
      let match;
      TS_IMPORT_PATTERN.lastIndex = 0;
      while ((match = TS_IMPORT_PATTERN.exec(file.content)) !== null) {
        const importPath = match[1];

        // Check against known fake packages
        for (const fakePkg of FAKE_NPM_PACKAGES) {
          if (importPath === fakePkg || importPath.startsWith(fakePkg + '/')) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('ai-hallucinations'),
              category: 'ai-hallucinations',
              severity: 'blocking',
              title: 'Non-existent Package',
              description: `The package '${importPath}' does not exist. This may be AI-hallucinated code.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
              suggestion: 'Verify this package exists on npm. Check the correct package name.',
              autoFixable: false,
            });
          }
        }

        // Check for relative imports that don't exist
        if (importPath.startsWith('./') || importPath.startsWith('../')) {
          const normalizedPath = resolveRelativePath(file.relativePath, importPath);
          const possibleExtensions = ['.ts', '.tsx', '.js', '.jsx', '/index.ts', '/index.tsx', '/index.js'];

          const exists = possibleExtensions.some((ext) =>
            existingFiles.has(normalizedPath.toLowerCase() + ext) ||
            existingFiles.has(normalizedPath.toLowerCase())
          );

          if (!exists && !importPath.includes('*')) {
            const lineNumber = getLineNumber(file.content, match.index);

            findings.push({
              id: generateFindingId('ai-hallucinations'),
              category: 'ai-hallucinations',
              severity: 'critical',
              title: 'Import Path Not Found',
              description: `The import path '${importPath}' does not resolve to an existing file.`,
              file: file.relativePath,
              line: lineNumber,
              code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
              suggestion: 'Verify the file exists or fix the import path.',
              autoFixable: false,
            });
          }
        }
      }
    }
  }

  return findings;
}

// Helper functions
function resolveRelativePath(fromFile: string, importPath: string): string {
  // Normalize path separators
  fromFile = fromFile.replace(/\\/g, '/');
  importPath = importPath.replace(/\\/g, '/');

  // Get directory of the importing file
  const fromDir = fromFile.substring(0, fromFile.lastIndexOf('/'));

  // Resolve the import path
  const parts = fromDir.split('/');

  for (const segment of importPath.split('/')) {
    if (segment === '..') {
      parts.pop();
    } else if (segment !== '.') {
      parts.push(segment);
    }
  }

  return parts.join('/');
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
